import { cloneDeep, isEmpty } from 'lodash'
import { jsx } from 'slate-hyperscript'
import { LIST_TYPES } from '../../../Element/list/utils'
import { v4 } from 'uuid'

/**
 * Get the nesting level from a list item element.
 * MS Word Online uses data-aria-level attribute to indicate list item depth.
 */
const getListItemLevel = (liElement: HTMLElement): number => {
  const level = liElement.getAttribute('data-aria-level')
  return level ? parseInt(level, 10) : 1
}

/**
 * Get the list style level from the list class.
 * MS Word Online uses BulletListStyle1, BulletListStyle2 for UL
 * and NumberListStyle1, NumberListStyle2 for OL
 */
const getListStyleLevel = (listElement: HTMLElement): number => {
  const className = listElement.getAttribute('class') || ''
  const bulletMatch = className.match(/BulletListStyle(\d+)/)
  const numberMatch = className.match(/NumberListStyle(\d+)/)
  if (bulletMatch) return parseInt(bulletMatch[1], 10)
  if (numberMatch) return parseInt(numberMatch[1], 10)
  return 1
}

/**
 * Check if an element is a list container (UL or OL)
 */
const isListElement = (node: any): boolean => {
  return node && (node.type === 'ul' || node.type === 'ol')
}

/**
 * Build nested list structure from flat list items with level information.
 * This handles the MS Word Online format where nested items have higher data-aria-level values.
 * 
 * Example: Items with levels [1, 1, 2, 2, 3, 3, 1] should produce:
 * - li (level 1)
 * - li (level 1)
 *   - ol/ul
 *     - li (level 2)
 *     - li (level 2)
 *       - ol/ul
 *         - li (level 3)
 *         - li (level 3)
 * - li (level 1)
 */
const buildNestedListFromItems = (items: any[], listType: string): any[] => {
  if (!items || items.length === 0) return []
  
  const result: any[] = []
  // Stack tracks: { level, containerChildren (the array to add siblings to), lastLi (last li at this level) }
  const stack: { level: number; containerChildren: any[]; lastLi: any }[] = [
    { level: 1, containerChildren: result, lastLi: null }
  ]
  
  items.forEach((item) => {
    const level = item._level || 1
    delete item._level
    
    // Pop stack until we find a level that can be the parent (level < item's level) or same level
    while (stack.length > 1 && stack[stack.length - 1].level > level) {
      stack.pop()
    }
    
    const current = stack[stack.length - 1]
    
    if (level === current.level) {
      // Same level - add as sibling
      current.containerChildren.push(item)
      current.lastLi = item
    } else if (level > current.level) {
      // Need to nest - find the last li at the current level to nest under
      const parentLi = current.lastLi
      
      if (parentLi && parentLi.type === 'li') {
        // If the parent li has inline/text children (not block elements), wrap them in a <p>
        // before adding the nested list. Mixed inline+block content in a li causes Slate's
        // normalizer to split each text node into its own block.
        const hasInlineChildren = parentLi.children.some(
          (c: any) => c.text !== undefined || c.type === 'a' || c.type === 'span'
        )
        if (hasInlineChildren) {
          parentLi.children = [{ type: 'p', attrs: {}, children: parentLi.children }]
        }
        // Create a new nested list inside the parent li
        const nestedList = { type: listType, attrs: {}, children: [item] }
        parentLi.children.push(nestedList)
        stack.push({ level, containerChildren: nestedList.children, lastLi: item })
      } else {
        // No parent li to nest into (shouldn't happen normally), add at current level
        current.containerChildren.push(item)
        current.lastLi = item
      }
    } else {
      // level < current.level - we've already popped to the right level, add as sibling
      current.containerChildren.push(item)
      current.lastLi = item
    }
  })
  
  return result
}

/**
 * Process list children to handle nesting based on data-aria-level attribute.
 * This is called when processing UL/OL elements.
 */
const processListChildrenForNesting = (children: any[], listElement: HTMLElement, listType: string): any[] => {
  // Check if any li has level info that indicates nesting is needed
  // Nesting is needed if there are items at different levels
  const levels = children
    .filter(child => child && child._level)
    .map(child => child._level)
  
  const uniqueLevels = [...new Set(levels)]
  
  if (uniqueLevels.length <= 1) {
    // All items at same level (or no level info), no nesting needed
    return children
  }
  
  return buildNestedListFromItems(children, listType)
}

/**
 * Merge consecutive lists at the body level into properly nested structures.
 * MS Word Online often creates separate list elements for nested items instead of nesting them.
 * This function merges consecutive lists of the same type with different style levels.
 */
const mergeNestedListsAtBodyLevel = (children: any[]): any[] => {
  if (!children || children.length === 0) return children
  
  const result: any[] = []
  let i = 0
  
  while (i < children.length) {
    const current = children[i]
    
    // Check if current is a list element
    if (current && isListElement(current)) {
      const listType = current.type
      const mergedList = { ...current, children: [...(current.children || [])] }
      
      // Look ahead for consecutive list elements of the same type that might be nested items
      let j = i + 1
      while (j < children.length) {
        const next = children[j]
        
        // Check if next is a list element of the same type
        if (next && isListElement(next) && next.type === listType) {
          // Check if this looks like a continuation (nested) list
          // MS Word Online creates separate lists for nested items
          const nextChildren = next.children || []
          
          // If the next list has items, check if they should be nested
          if (nextChildren.length > 0) {
            const lastLi = mergedList.children[mergedList.children.length - 1]
            
            // Check if next list's first item has a higher level marker
            // If no explicit level, check the list's class for style level
            const shouldNest = nextChildren.some((child: any) => child && child._level && child._level > 1)
            
            if (shouldNest && lastLi && lastLi.type === 'li') {
              // Nest this list's items inside the last li of the previous list
              const nestedList = { type: listType, attrs: {}, children: nextChildren.map((c: any) => {
                if (c && c._level) {
                  const { _level, ...rest } = c
                  return rest
                }
                return c
              })}
              lastLi.children.push(nestedList)
            } else {
              // Not a nested list, add items to the current list
              mergedList.children.push(...nextChildren.map((c: any) => {
                if (c && c._level) {
                  const { _level, ...rest } = c
                  return rest
                }
                return c
              }))
            }
          }
          j++
        } else {
          break
        }
      }
      
      result.push(mergedList)
      i = j
    } else {
      result.push(current)
      i++
    }
  }
  
  return result
}

export const spanningAttrs = (el: HTMLElement) => {
  const attrs = {}
  const rowSpan = parseInt(el.getAttribute('rowspan') ?? '1')
  const colSpan = parseInt(el.getAttribute('colspan') ?? '1')
  if (rowSpan > 1) attrs['rowSpan'] = rowSpan
  if (colSpan > 1) attrs['colSpan'] = colSpan

  return attrs
}

export const emptyCell = (cellType, attrs = {}) => {
  return jsx('element', { type: cellType, attrs: { void: true, ...attrs } }, [{ text: '' }])
}


export const addVoidCellsAndApplyAttributes = (rows) => {
  rows.forEach((row, currIndex) => {
    const cells = row.children

    cells.forEach((cell, cellIndex) => {
      if (!cell || !cell.attrs) return

      const { rowSpan, inducedRowSpan } = cell.attrs

      let span = rowSpan ?? inducedRowSpan ?? 0

      if (!span || span < 2) return
      const nextRow = rows[currIndex + 1]
      if (!nextRow) {
        delete cell?.attrs?.inducedRowSpan
        return
      }

      // set inducedRowSpan on cell in row at cellIndex
      span--
      nextRow?.children?.splice(cellIndex, 0,
        emptyCell('td',
          (span > 1) ? { inducedRowSpan: span } : {}
        ))

      // Include next row in trgrp
      nextRow['attrs']['included'] = true

      // Make a new trgrp
      if (rowSpan) {
        row['attrs']['origin'] = true
      }

      delete cell?.['attrs']?.['inducedRowSpan']

    })
  })
}

export const getTbodyChildren = (rows) => {
  const newTbodyChildren = rows.reduce((tBodyChildren, row, rowIndex) => {

    const { included, origin } = row.attrs
    const l = tBodyChildren.length

    if (included || origin) {

      if (origin && !included) {
        tBodyChildren.push(jsx('element', { type: 'trgrp' }, row))
      }
      if (included) {
        tBodyChildren[l - 1].children.push(row)

      }
      delete row['attrs']['included']
      delete row['attrs']['origin']
      return tBodyChildren
    }

    tBodyChildren.push(row)
    return tBodyChildren

  }, [])
  return newTbodyChildren
}

const ELEMENT_TAGS = {
  A: el => ({ type: 'a', attrs: { url: el.getAttribute('href') || "#" } }),
  H1: () => ({ type: 'h1', attrs: {} }),
  H2: () => ({ type: 'h2', attrs: {} }),
  H3: () => ({ type: 'h3', attrs: {} }),
  H4: () => ({ type: 'h4', attrs: {} }),
  H5: () => ({ type: 'h5', attrs: {} }),
  H6: () => ({ type: 'h6', attrs: {} }),
  IMG: (el) => {
    return { type: 'img', attrs: { url: el.getAttribute('src') } }
  },
  LI: (el) => ({ type: 'li', attrs: {}, _level: getListItemLevel(el) }),
  OL: () => ({ type: 'ol', attrs: {} }),
  P: () => ({ type: 'p', attrs: {} }),
  PRE: () => ({ type: 'code', attrs: {} }),
  UL: () => ({ type: 'ul', attrs: {} }),
  IFRAME: (el) => ({ type: 'embed', attrs: { url: el.getAttribute('href') } }),
  TABLE: (el) => ({ type: 'table', attrs: {} }),
  THEAD: (el) => ({ type: 'thead', attrs: {} }),
  TBODY: (el) => ({ type: 'tbody', attrs: {} }),
  TR: (el) => ({ type: 'tr', attrs: {} }),
  TD: (el) => ({ type: 'td', attrs: {} }),
  TH: (el) => ({ type: 'th', attrs: {} }),
}

const getNextSibling = (elem) => {
  let allOLElements = []
  var sibling = elem?.nextElementSibling
  while (sibling?.getAttribute?.('class')?.includes('ListContainerWrapper')) {
    if (sibling && sibling.children[0]?.nodeName === 'OL') {
      allOLElements.push(sibling.children[0])
    }
    sibling = sibling?.nextElementSibling
  }
  return allOLElements
};

const generateId = () => v4().split('-').join('')

const isInlineOrTextNode = (child) => {
  if (!child) return false

  if (child.hasOwnProperty('text')) return true

  if (Array.isArray(child)) return child.every(isInlineOrTextNode)

  if (child.type && ['a', 'span'].includes(child.type)) return true
  return false
}

const wrapInlineChildrenInParagraph = (children) => {
  if (!children || children.length === 0) return children
  
  const allInline = children.every(isInlineOrTextNode)
  
  if (allInline) {
    const flatChildren = children.flat().filter(c => c !== null && c !== undefined)
    if (flatChildren.length === 0) return [{ type: 'p', attrs: {}, children: [{ text: '' }] }]
    return [jsx('element', { type: 'p', attrs: {} }, flatChildren)]
  }
  
  // Mixed content: group consecutive inline elements into paragraphs
  const result = []
  let currentInlineGroup = []
  
  children.forEach((child) => {
    if (isInlineOrTextNode(child)) {
      currentInlineGroup.push(...(Array.isArray(child) ? child : [child]))
    } else {
      if (currentInlineGroup.length > 0) {
        result.push(jsx('element', { type: 'p', attrs: {} }, currentInlineGroup))
        currentInlineGroup = []
      }
      result.push(child)
    }
  })
  
  if (currentInlineGroup.length > 0) {
    result.push(jsx('element', { type: 'p', attrs: {} }, currentInlineGroup))
  }
  
  return result
}

export const deserialize = (el) => {
  if (el.nodeType === 3) {
    if (
      el.parentElement.nodeName === 'BODY'
    ) {
      return null
    }
    return el.textContent
  } else if (el.nodeType !== 1 || el.nodeName === 'META') {
    return null
  } else if (el.nodeName === 'BR') {
    return { text: '\n', break: false, separaterId: generateId() }
  } else if (el.nodeName === 'O:P') {
    if (el.childNodes?.length) {
      return { text: '' }
    }
    return null
  }

  const { nodeName } = el
  let parent = el

  if (
    nodeName === 'PRE' &&
    el.childNodes[0] &&
    el.childNodes[0]?.nodeName === 'CODE'
  ) {
    parent = el.childNodes[0]
  }

  let children = Array.from(parent.childNodes).map(deserialize).flat()
  children = children.filter((child) => child !== null)
  children = children.map((child) => {
    if (typeof child === 'string') {
      return child.replace(/\n/g, ' ')
    } else return child
  })
  if (children.length === 0) {
    if (nodeName === "A") {
      children = [{ text: '\n', break: false }]
    }
    else {
      children = [{ text: '' }]
    }
  }

  if (el.nodeName === 'BODY') {
    // Wrap inline/text children in paragraphs for proper Slate structure
    children = wrapInlineChildrenInParagraph(children)
    // Process consecutive lists to merge nested items properly
    children = mergeNestedListsAtBodyLevel(children)
    return jsx('fragment', {}, [...children])
  }

  if (el.nodeName === 'DIV' && el.getAttribute('class')?.includes('ListContainerWrapper')) {
    // Check if there's a previous ListContainerWrapper sibling
    const prevSibling = el.previousElementSibling
    if (prevSibling?.getAttribute?.('class')?.includes('ListContainerWrapper')) {
      // This is a subsequent item in a series - skip it (first one handles all siblings)
      return
    }
    // First ListContainerWrapper or single item - continue processing
  }

  if (el.nodeName === "SPAN" && el.getAttribute('data-ccp-parastyle') === 'heading 1') {
    return jsx('element', { type: 'h1', attrs: {} }, children)
  }


  if (el.nodeName === "SPAN" && el.getAttribute('data-ccp-parastyle') === 'heading 2') {
    return jsx('element', { type: 'h2', attrs: {} }, children)
  }

  if (el.nodeName === "SPAN" && el.getAttribute('data-ccp-parastyle') === 'heading 3') {
    return jsx('element', { type: 'h3', attrs: {} }, children)
  }

  if (el.nodeName === "SPAN" && (el.getAttribute('data-ccp-parastyle') === 'Quote' || el.getAttribute('data-ccp-parastyle') === 'Intense Quote')) {
    return jsx('element', { type: 'blockquote', attrs: {} }, children)
  }

  // Word Online heading paragraphs: <p role="heading" aria-level="N"> contains spans with
  // data-ccp-parastyle="heading N". When a heading has multiple text runs, each span gets
  // individually converted to a heading block by the data-ccp-parastyle handlers above,
  // which Slate normalization then splits into separate heading nodes — breaking "Heading
  // to check" into two separate headings, for example.
  // Fix: detect the heading <p>, flatten all heading-block children back to their text
  // content, and emit a single heading element.
  if (el.nodeName === 'P' && el.getAttribute('role') === 'heading') {
    const ariaLevel = parseInt(el.getAttribute('aria-level') || '1', 10)
    const level = Math.min(Math.max(ariaLevel, 1), 6)
    const headingType = `h${level}`

    const flatChildren: any[] = []
    for (const child of children) {
      if (child?.type && ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(child.type)) {
        flatChildren.push(...(child.children || [{ text: '' }]))
      } else {
        flatChildren.push(child)
      }
    }

    const headingChildren = [...flatChildren]
    while (headingChildren.length > 1) {
      const last = headingChildren[headingChildren.length - 1]
      if (last?.text !== undefined && last.text.replace(/[ \s]/g, '') === '') {
        headingChildren.pop()
      } else {
        break
      }
    }

    return jsx('element', { type: headingType, attrs: {} }, headingChildren.length > 0 ? headingChildren : [{ text: '' }])
  }

  if (ELEMENT_TAGS[nodeName]) {
    let attrs = ELEMENT_TAGS[nodeName](el)

    if (nodeName === 'OL' && el?.parentElement?.nodeName === 'DIV' && el.parentElement?.getAttribute('class')?.includes('ListContainerWrapper')) {
      // Check if this is the first ListContainerWrapper (no previous sibling with ListContainerWrapper)
      const prevSibling = el.parentElement?.previousElementSibling
      const isFirstInSeries = !prevSibling?.getAttribute?.('class')?.includes('ListContainerWrapper')
      
      // Check if there are sibling ListContainerWrappers (nested list scenario)
      const nextSibling = el.parentElement?.nextElementSibling
      const hasNestedSiblings = nextSibling?.getAttribute?.('class')?.includes('ListContainerWrapper')
      
      // Handle single list item case - no siblings
      if (isFirstInSeries && !hasNestedSiblings) {
        // Just clean up _level and return the list
        const cleanChildren = children.filter(c => c && c.type === 'li').map(child => {
          if (child && child._level) {
            const { _level, ...rest } = child
            return rest
          }
          return child
        })
        if (cleanChildren.length > 0) {
          return jsx('element', { type: 'ol', attrs: {} }, cleanChildren)
        }
        // If no li children, still return list with whatever children we have
        if (children.length > 0) {
          return jsx('element', { type: 'ol', attrs: {} }, children)
        }
      }
      
      if (isFirstInSeries && hasNestedSiblings) {
        // This is the first OL in a series of ListContainerWrappers with nested items
        // Collect all sibling OLs and build nested structure based on NumberListStyle level
        const firstStyleLevel = getListStyleLevel(el)
        let olChildren = children.filter(c => c && c.type === 'li').map(c => {
          c._level = firstStyleLevel
          return c
        })
        
        let sibling = el.parentElement?.nextElementSibling
        while (sibling?.getAttribute?.('class')?.includes('ListContainerWrapper')) {
          const siblingOl = sibling.children[0]
          if (siblingOl?.nodeName === 'OL') {
            const styleLevel = getListStyleLevel(siblingOl as HTMLElement)
            const siblingLi = siblingOl.children[0]
            if (siblingLi?.nodeName === 'LI') {
              const liElement = deserialize(siblingLi)
              if (liElement) {
                // Mark with style level for nesting
                liElement._level = styleLevel
                olChildren.push(liElement)
              }
            }
          }
          sibling = sibling.nextElementSibling
        }
        
        // Process for nesting (only if there are multiple levels)
        const processedChildren = processListChildrenForNesting(olChildren, el, 'ol')
        const cleanChildren = processedChildren.map(child => {
          if (child && child._level) {
            const { _level, ...rest } = child
            return rest
          }
          return child
        })
        
        // Always return the list even if it has just one item
        if (cleanChildren.length > 0) {
          return jsx('element', { type: 'ol', attrs: {} }, cleanChildren)
        }
      } else if (!isFirstInSeries) {
        // This is a subsequent OL in a ListContainerWrapper series - skip it (handled by the first one)
        return
      }
      // Single item or first item without nested siblings - fall through to default handling
    }

    // Handle UL in ListContainerWrapper (same pattern as OL for Word Online bullet lists)
    if (nodeName === 'UL' && el?.parentElement?.nodeName === 'DIV' && el.parentElement?.getAttribute('class')?.includes('ListContainerWrapper')) {
      // Check if this is the first ListContainerWrapper (no previous sibling with ListContainerWrapper)
      const prevSibling = el.parentElement?.previousElementSibling
      const isFirstInSeries = !prevSibling?.getAttribute?.('class')?.includes('ListContainerWrapper')
      
      // Check if there are sibling ListContainerWrappers (nested list scenario)
      const nextSibling = el.parentElement?.nextElementSibling
      const hasNestedSiblings = nextSibling?.getAttribute?.('class')?.includes('ListContainerWrapper')
      
      // Handle single list item case - no siblings
      if (isFirstInSeries && !hasNestedSiblings) {
        // Just clean up _level and return the list
        const cleanChildren = children.filter(c => c && c.type === 'li').map(child => {
          if (child && child._level) {
            const { _level, ...rest } = child
            return rest
          }
          return child
        })
        if (cleanChildren.length > 0) {
          return jsx('element', { type: 'ul', attrs: {} }, cleanChildren)
        }
        // If no li children, still return list with whatever children we have
        if (children.length > 0) {
          return jsx('element', { type: 'ul', attrs: {} }, children)
        }
      }
      
      if (isFirstInSeries && hasNestedSiblings) {
        // This is the first UL in a series of ListContainerWrappers with nested items
        // Collect all sibling ULs and build nested structure based on BulletListStyle level
        const firstStyleLevel = getListStyleLevel(el)
        let ulChildren = children.filter(c => c && c.type === 'li').map(c => {
          c._level = firstStyleLevel
          return c
        })
        
        let sibling = el.parentElement?.nextElementSibling
        while (sibling?.getAttribute?.('class')?.includes('ListContainerWrapper')) {
          const siblingUl = sibling.children[0]
          if (siblingUl?.nodeName === 'UL') {
            const styleLevel = getListStyleLevel(siblingUl as HTMLElement)
            const siblingLi = siblingUl.children[0]
            if (siblingLi?.nodeName === 'LI') {
              const liElement = deserialize(siblingLi)
              if (liElement) {
                // Mark with style level for nesting
                liElement._level = styleLevel
                ulChildren.push(liElement)
              }
            }
          }
          sibling = sibling.nextElementSibling
        }
        
        // Process for nesting (only if there are multiple levels)
        const processedChildren = processListChildrenForNesting(ulChildren, el, 'ul')
        const cleanChildren = processedChildren.map(child => {
          if (child && child._level) {
            const { _level, ...rest } = child
            return rest
          }
          return child
        })
        
        // Always return the list even if it has just one item
        if (cleanChildren.length > 0) {
          return jsx('element', { type: 'ul', attrs: {} }, cleanChildren)
        }
      } else if (!isFirstInSeries) {
        // This is a subsequent UL in a ListContainerWrapper series - skip it (handled by the first one)
      return
      }
      // Single item or first item without nested siblings - fall through to default handling
    }

    // Handle nested lists for UL based on data-aria-level attribute (for UL not in ListContainerWrapper)
    if (nodeName === 'UL' && !(el?.parentElement?.nodeName === 'DIV' && el.parentElement?.getAttribute('class')?.includes('ListContainerWrapper'))) {
      const listType = 'ul'
      
      // Filter to only include li children and preserve their level info
      const liChildren = children.filter(child => child && child.type === 'li')
      
      // Check if nesting is needed (multiple levels present)
      const levels = liChildren.filter(c => c && c._level).map(c => c._level)
      const uniqueLevels = [...new Set(levels)]
      
      if (uniqueLevels.length > 1) {
        // Process children for nesting based on _level attribute
        const processedChildren = processListChildrenForNesting(liChildren, el, listType)
        
        // Clean up _level from children
        const cleanChildren = processedChildren.map(child => {
          if (child && child._level) {
            const { _level, ...rest } = child
            return rest
          }
          return child
        })
        
        if (cleanChildren.length > 0) {
          return jsx('element', { type: listType, attrs: {} }, cleanChildren)
        }
      }
      // For single-level lists, clean up _level and return
      const cleanChildren = liChildren.map(child => {
        if (child && child._level) {
          const { _level, ...rest } = child
          return rest
        }
        return child
      })
      if (cleanChildren.length > 0) {
        return jsx('element', { type: listType, attrs: {} }, cleanChildren)
      }
    }

    if (nodeName === 'TABLE') {
      let row = 0,
        col
      row = el.querySelectorAll('TR').length
      const colElementLength = el.getElementsByTagName('COLGROUP')[0]?.children?.length ?? 0
      col = Math.max(...Array.from(el.getElementsByTagName('TR') as HTMLElement[]).map(row => row.children.length), colElementLength)

      let colWidths: Array<any> = Array.from({ length: col }).fill(250)

      Array.from(el.childNodes).forEach((child: any) => {
        if (child?.nodeName === 'COLGROUP') {
          let colGroupWidth = Array<number>(col).fill(250)
          let totalWidth = parseFloat(child.getAttribute('data-width')) || col * 250
          Array.from(child.children).forEach((child: any, index) => {
            if (child?.nodeName === 'COL') {
              let width = child?.style?.width ?? '250px'
              if (width.substr(-1) === '%') {
                colGroupWidth[index] = (parseFloat(width) * totalWidth) / 100
              } else if (width.substr(-2) === 'px') {
                colGroupWidth[index] = parseFloat(width)
              }
            }
          })
          colWidths = colGroupWidth
        }
      })

      let tableHead
      let tableBody

      children.forEach((tableChild) => {
        if (tableChild?.type === 'thead') {
          tableHead = tableChild
          return
        }
        if (tableChild?.type === 'tbody') {
          tableBody = tableChild
          return
        }
      });

      let disabledCols = [...tableHead?.attrs?.disabledCols ?? [], ...tableBody?.attrs?.disabledCols ?? []]
      delete tableHead?.attrs?.disabledCols
      delete tableBody?.attrs?.disabledCols

      attrs = {
        ...attrs,
        attrs: {
          ...attrs['attrs'],
          rows: row,
          cols: col,
          colWidths: colWidths,
          ...(disabledCols.length && { disabledCols: Array.from(new Set(disabledCols)) })
        }
      }
    }

    if (["THEAD", "TBODY"].includes(nodeName)) {
      const rows = children
      const disabledCols = rows.flatMap(row => {
        const { disabledCols } = row.attrs
        delete row['attrs']['disabledCols']
        return disabledCols ?? []
      })
      attrs.attrs['disabledCols'] = disabledCols
    }

    if (nodeName === "TBODY") {

      addVoidCellsAndApplyAttributes(children)

      children = getTbodyChildren(children)
    }

    if (nodeName === "TR") {
      const cells = children.filter(child => ['th', 'td'].includes(child.type))

      const disabledCols = cells.flatMap((cell, cellIndex) => {
        let { colSpan } = cell.attrs
        if (!colSpan) return []
        colSpan = parseInt(colSpan)
        return Array(colSpan).fill(0).map((_, i) => cellIndex + i)
      })

      if (disabledCols.length)
        attrs.attrs['disabledCols'] = disabledCols
    }

    if (nodeName === "TD" || nodeName === "TH") {
      let textFormat = {}
      if (el.style?.['font-weight'] === 'bold') {
        textFormat = { ...textFormat, bold: true }
      }

      if (el.style?.['font-style'] === 'italic') {
        textFormat = { ...textFormat, italic: true }
      }

      if (el.style?.['text-decoration'] === 'underline') {
        textFormat = { ...textFormat, underline: true }
      }

      if (el.style?.['text-decoration'] === 'line-through') {
        textFormat = { ...textFormat, strikethrough: true }
      }
      const spannedAttrs = spanningAttrs(el)

      if (!isEmpty(spannedAttrs)) {
        attrs = {
          ...attrs,
          attrs: {
            ...attrs['attrs'],
            ...spannedAttrs,
            "redactor-attributes": spannedAttrs
          },
        }
      }

      let childrenCopy = cloneDeep(children)
      let finalTdChildren = Array.from(childrenCopy).map((child) => {
        if (child['type']) {
          return child
        }
      })
      finalTdChildren = finalTdChildren.filter((child) => child !== undefined)

      children = finalTdChildren.map((child: any) => {
        if (typeof child === 'string' || child.nodeType === 3) return jsx('text', { ...textFormat }, child)
        else if (child.text !== undefined && child.text !== '') {
          const newChild = {
            ...child,
            attrs: { ...child.attrs },
            ...textFormat
          }
          return newChild
        } else {
          return child
        }
      })

      const { colSpan = 1, rowSpan } = attrs?.['attrs']

      const tableCell = [
        jsx('element', attrs, children),
        ...Array(colSpan - 1)
          .fill(0)
          .map((_) => emptyCell(nodeName.toLowerCase(), rowSpan ? { inducedRowSpan: rowSpan } : {}))
      ]
      return tableCell
    }
    
    // For LI elements, preserve the _level attribute on the element for nesting logic
    if (nodeName === 'LI') {
      const level = attrs._level
      const { _level, ...cleanAttrs } = attrs

      // Word Online wraps each text run in its own <p> tag inside <li>.
      // When the li also has a nested ol/ul, withListItem normalizer wraps each individual
      // text node in a separate `fragment`, causing each run to render as a separate block.
      // Fix: merge all consecutive inline-only <p> children into a single <p> so the
      // normalizer sees one Element child (not multiple text nodes) alongside the nested list.
      const isInlineOnly = (pChildren: any[]) =>
        pChildren.every((c: any) => c.text !== undefined || c.type === 'a' || c.type === 'span')

      let mergedInlineChildren: any[] = []
      let processedChildren: any[] = []

      children.forEach((child: any) => {
        if (child?.type === 'p') {
          const pChildren = child.children || []
          if (isInlineOnly(pChildren)) {
            mergedInlineChildren.push(...pChildren)
            return
          }
        }
        // Non-inline-p child: flush accumulated inline runs into one <p> first
        if (mergedInlineChildren.length > 0) {
          processedChildren.push({ type: 'p', attrs: {}, uid: '', children: mergedInlineChildren })
          mergedInlineChildren = []
        }
        processedChildren.push(child)
      })

      // Flush any remaining inline runs
      if (mergedInlineChildren.length > 0) {
        if (processedChildren.length === 0) {
          // li has only inline-p children (no nested list) — use text nodes directly
          processedChildren.push(...mergedInlineChildren)
        } else {
          processedChildren.push({ type: 'p', attrs: {}, uid: '', children: mergedInlineChildren })
        }
      }

      // Remove trailing whitespace-only text nodes
      processedChildren = processedChildren.filter((child: any, index: number) => {
        if (index < processedChildren.length - 1) return true
        if (child.text !== undefined && child.text.trim() === '') return false
        return true
      })

      if (processedChildren.length === 0) {
        processedChildren = [{ text: '' }]
      }

      const liElement = jsx('element', cleanAttrs, processedChildren) as any
      if (level) {
        liElement._level = level
      }
      return liElement
    }
    
    // For OL elements that fall through (single items, etc.), clean up _level from children
    if (nodeName === 'OL') {
      const cleanChildren = children.map(child => {
        if (child && child._level) {
          const { _level, ...rest } = child
          return rest
        }
        return child
      })
      return jsx('element', attrs, cleanChildren)
    }
    
    return jsx('element', attrs, children)
  }

  if (el?.nodeName === 'SPAN') {
    let attrs: any = {}
    let textFormat = {}

    children = children.map((child) => {
      if (typeof child === 'string') {
        return child.replace(/\n/g, ' ')
      } else return child
    })

    //to skip all styles from children of <a>
    // Only add color style, ignore font-size, line-height, font-family from Word Online
    if (el.style['color'] && el.closest('a') === null) {
      attrs = {
        style: {
          color: el.style['color']
        }
      }
    }
    if (el.getAttribute('class')?.includes('Bold') || (el?.style?.['font-weight'] && el.style['font-weight'] === 'bold')) {
      textFormat = { ...textFormat, bold: true }
    }

    if (el.style['font-style'] === 'italic') {
      textFormat = { ...textFormat, italic: true }
    }

    if (el.getAttribute('class')?.includes('Underlined')) {
      textFormat = { ...textFormat, underline: true }
    }

    if (el.getAttribute('class')?.includes('Strikethrough')) {
      textFormat = { ...textFormat, strikethrough: true }
    }

    if (el.getAttribute('class')?.includes('Subscript')) {
      textFormat = { ...textFormat, subscript: true }
    }

    if (el.getAttribute('class')?.includes('Superscript')) {
      textFormat = { ...textFormat, superscript: true }
    }

    children = children.map((child) => {
      if (typeof child === 'string' || child.nodeType === 3) {
        // Only include attrs if there's actual style content
        const textProps = Object.keys(attrs).length > 0 ? { attrs, ...textFormat } : { ...textFormat }
        return jsx('text', textProps, child)
      }
      else if (child.text !== undefined) {
        // Only merge attrs if there's actual content
        const mergedAttrs = Object.keys(attrs).length > 0 
          ? { ...child.attrs, ...attrs }
          : child.attrs
        const newChild = {
          ...child,
          ...(mergedAttrs && Object.keys(mergedAttrs).length > 0 ? { attrs: mergedAttrs } : {}),
          ...textFormat
        }

        return newChild
      } else {
        return child
      }
    })
  }

  return children
}